// Constellation palette — mirrors Aether tokens (packages/aether/tokens).
// Shared by the graph canvas and the control-plane legend.
export const AE = {
  gold: '#c9a96e', // --ae-brand-gold      (constellation lines)
  goldSoft: '#e2bc77', // --ae-color-gold-300 (studio)
  parchment: '#e8e2d9', // --ae-brand-parchment (star core)
  ash: '#7a7268', // --ae-brand-ash
  blue: '#60a5fa', // --ae-color-blue-400   (fund glow)
  crimson: '#c0392b', // --ae-brand-crimson    (FI glow)
  green: '#4ade80', // --ae-color-green-400  (signal glow)
  violet: '#a78bfa', // VC funds
  teal: '#2dd4bf', // markets
  amber: '#f0a35e', // media
} as const

/**
 * You. Sampled from the Diwa Domains mark (#de4f46) and snapped to --ae-color-crimson-400.
 *
 * The ego is the only node with a *coloured core* — every other star is parchment with a coloured
 * halo. A categorical difference, not a gradient one, so "you" reads at thumbnail size. Red is
 * reserved: FI keeps the deeper crimson glow, but nothing else owns a red core.
 *
 * When node colours become user-customisable this is the first thing to lift into a theme.
 */
export const EGO = {
  core: '#e0564a', // --ae-color-crimson-400 — the dot in the logo
  halo: '#f48a7f', // --ae-color-crimson-300
} as const

export type Group = 'bridge' | 'fund' | 'fi' | 'vc' | 'markets' | 'media' | 'studio' | 'signal' | 'other'

export const GLOW: Record<Group, string> = {
  bridge: EGO.halo,
  fund: AE.blue,
  fi: AE.crimson,
  vc: AE.violet,
  markets: AE.teal,
  media: AE.amber,
  studio: AE.goldSoft,
  signal: AE.green,
  other: AE.ash,
}

/**
 * Domain hues, cycled. The wedge rim, the hover card, the galaxy points, and the View-tab legend
 * all read from `domainColorMap` so a domain wears exactly one colour everywhere it appears.
 */
export const DOMAIN_PALETTE = [AE.blue, AE.teal, AE.goldSoft, AE.violet, AE.amber, AE.green] as const

/**
 * Light-mode domain hues. The dark palette's pastels (teal #2dd4bf, amber #f0a35e, …) wash out on
 * a parchment ground, so light mode swaps in darker, more saturated siblings — same order, same
 * position, so a domain keeps the "same" family colour across modes, just legible on paper. Used
 * for the landing hero constellation when data-mode='light'.
 */
export const DOMAIN_PALETTE_LIGHT = ['#2563eb', '#0f766e', '#9a6f16', '#6d28d9', '#b45309', '#15803d'] as const

/**
 * The STAR-CHART hues — the four separated domain colours of the B+C marketing synthesis
 * (dusk violet · sea-glass teal · burnished gold · ember copper). Muted and earthen so
 * crimson stays the only loud voice in the sky; the marketing hero and the /demo sky both
 * speak this palette so the public surfaces read as one instrument.
 */
export const STARCHART = {
  network: '#9b85c4', // dusk violet — people & orgs
  markets: '#6fa89d', // sea-glass teal — markets & finance
  govcon: '#c9a96e', // burnished gold — govcon & pipelines
  media: '#c77b52', // ember copper — media & library
} as const

/** Darker star-chart siblings that hold on the parchment (light-mode) ground. */
export const STARCHART_LIGHT = {
  network: '#5f4a94',
  markets: '#2f6b60',
  govcon: '#8a6a2a',
  media: '#9c5230',
} as const

// slug keywords → which star-chart family a domain reads as
const STARCHART_FAMILIES: Array<{ family: keyof typeof STARCHART; match: RegExp }> = [
  { family: 'govcon', match: /gov|contract|pipeline|award/ },
  { family: 'markets', match: /market|finance|invest|fund|stock|trading/ },
  { family: 'media', match: /media|anime|music|film|book|library|shelf|podcast|game/ },
  { family: 'network', match: /people|network|crm|creator|person|org|social/ },
]

/**
 * A star-chart hue map: recognised slugs land on their SEMANTIC family colour (a govcon
 * region is always burnished gold, in any world), the rest cycle the remaining hues in the
 * same stable sorted order `domainColorMap` uses. Never hands two overlapping families the
 * same hue until all four are spoken for.
 */
export function starchartColorMap(
  slugs: Iterable<string>,
  mode: 'light' | 'dark' = 'dark',
): Map<string, string> {
  const hues = mode === 'light' ? STARCHART_LIGHT : STARCHART
  const order: Array<keyof typeof STARCHART> = ['network', 'markets', 'govcon', 'media']
  const sorted = [...new Set(slugs)].sort()
  const taken = new Set<keyof typeof STARCHART>()
  const m = new Map<string, string>()
  // first pass: semantic matches claim their family
  for (const slug of sorted) {
    const hit = STARCHART_FAMILIES.find((f) => f.match.test(slug.toLowerCase()))
    if (hit && !taken.has(hit.family)) {
      taken.add(hit.family)
      m.set(slug, hues[hit.family])
    }
  }
  // second pass: everyone else cycles the leftover hues (then wraps the full order)
  const leftover = order.filter((f) => !taken.has(f))
  let i = 0
  for (const slug of sorted) {
    if (m.has(slug)) continue
    const family = leftover.length > 0 ? leftover[i % leftover.length] : order[i % order.length]
    m.set(slug, hues[family])
    i++
  }
  return m
}

/**
 * Stable domain→hue map. Slugs are de-duplicated and sorted before the palette is cycled, so the
 * mapping is identical no matter which caller (canvas or legend) built it or what happens to be on
 * screen. Feed it the SAME slug set from every call site (live domains ∪ node domains) and the
 * legend can never drift from the sky. Pass a `palette` (e.g. DOMAIN_PALETTE_LIGHT) to re-tint for
 * a light ground while keeping the exact same slug→index mapping.
 */
export function domainColorMap(
  slugs: Iterable<string>,
  palette: readonly string[] = DOMAIN_PALETTE,
): Map<string, string> {
  const m = new Map<string, string>()
  let i = 0
  for (const slug of [...new Set(slugs)].sort()) m.set(slug, palette[i++ % palette.length])
  return m
}

/**
 * A user colour override lives at `entity.attributes.color`. Returns a validated hex string
 * (`#rgb`/`#rrggbb`/`#rrggbbaa`) or null — bad data must never reach a `fill`/`stroke` and break the
 * render. This is the *color resolution* precedence: an override, when present, wins over any
 * domain/world default and — like the ego's crimson core — colours the star's CORE, not just its halo.
 *
 * numen/3D DEFERRED: the sovereign 3D "numen" scene will read this same override so a star keeps its
 * colour across 2D and 3D. That renderer isn't built yet; this task is 2D only. When it lands it must
 * call `readNodeColor` too rather than re-deriving colour, or the two views will disagree.
 */
export function readNodeColor(attributes: Record<string, unknown> | null | undefined): string | null {
  const c = attributes?.color
  if (typeof c !== 'string') return null
  const v = c.trim()
  return /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(v) ? v : null
}

/** A hand-pinned star position, stored at `entity.attributes.pos` as WORLD-CENTER OFFSETS. */
export interface NodePos {
  x: number
  y: number
}

/**
 * A user pin lives at `entity.attributes.pos` as `{x, y}` offsets from the sky's center (the
 * ego), NOT absolute canvas pixels — the world square resizes with reach and population, so an
 * absolute coordinate would drift between sessions while a center offset stays put. Returns a
 * validated pair or null — bad data must never poison the simulation with NaN positions.
 */
export function readNodePos(attributes: Record<string, unknown> | null | undefined): NodePos | null {
  const p = attributes?.pos
  if (typeof p !== 'object' || p === null || Array.isArray(p)) return null
  const { x, y } = p as Record<string, unknown>
  if (typeof x !== 'number' || typeof y !== 'number') return null
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null
  return { x, y }
}

/**
 * The attributes patch that persists a pin — the body for PATCH /graph/entities/{id}
 * (`{attributes: posPatch(x, y)}`; the backend merges top-level keys, so `pos` is replaced
 * wholesale and everything else survives). Coordinates are center offsets, rounded to 0.1px —
 * sub-pixel noise is sim jitter, not intent.
 */
export function posPatch(x: number, y: number): { pos: NodePos } {
  return { pos: { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 } }
}

/** Live population of one region: how many stars it holds and when it was last touched. */
export interface DomainStat {
  count: number
  /** Epoch ms of the freshest node (max of updated_at, created_at), or null if unknown. */
  lastTouched: number | null
}

/**
 * Per-domain entity count + last-touch, computed from the live graph payload. Powers the domain
 * picker's count and its activity dot — no fixed taxonomy, just what the graph actually holds.
 */
export function domainStats(
  nodes: Array<{ domain: string; created_at?: string | null; updated_at?: string | null }>,
): Map<string, DomainStat> {
  const m = new Map<string, DomainStat>()
  for (const n of nodes) {
    const cur = m.get(n.domain) ?? { count: 0, lastTouched: null }
    cur.count += 1
    const t = Math.max(
      n.updated_at ? Date.parse(n.updated_at) || 0 : 0,
      n.created_at ? Date.parse(n.created_at) || 0 : 0,
    )
    if (t > 0 && (cur.lastTouched == null || t > cur.lastTouched)) cur.lastTouched = t
    m.set(n.domain, cur)
  }
  return m
}

/** How recent a touch still reads as "a spring just ran here". */
export const FRESH_WINDOW_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Is a region warm? A spring run manifests as freshly created/updated nodes, so "recently touched"
 * is a faithful proxy with no extra request. Archived or empty regions always read idle (grey).
 */
export function domainActivity(opts: {
  count: number
  archived?: boolean
  lastTouched: number | null
  now?: number
  windowMs?: number
}): 'active' | 'idle' {
  if (opts.archived || opts.count === 0 || opts.lastTouched == null) return 'idle'
  const now = opts.now ?? Date.now()
  const windowMs = opts.windowMs ?? FRESH_WINDOW_MS
  return now - opts.lastTouched <= windowMs ? 'active' : 'idle'
}

/** The activity dot's fill: green = a spring recently ran, ash = empty/archived/stale. */
export const ACTIVITY_DOT = { active: AE.green, idle: AE.ash } as const

/**
 * Multi-domain scope that KEEPS cross-domain bridges. Nodes whose domain is in the selected set
 * (the union), PLUS the ego, PLUS any node one hop away from a selected-domain node (so a region's
 * bridges out survive). An empty set means "all domains" and returns the graph untouched.
 *
 * Generic over node/edge shape so it stays a pure, testable function with no import cycle.
 */
export function scopeToDomains<
  N extends { id: string; domain: string; self?: boolean | null },
  E extends { source: string; target: string },
>(data: { nodes: N[]; edges: E[] }, domains: Set<string>): { nodes: N[]; edges: E[] } {
  if (domains.size === 0) return data
  const inDomain = new Set(data.nodes.filter((n) => domains.has(n.domain)).map((n) => n.id))
  const edges = data.edges.filter((e) => inDomain.has(e.source) || inDomain.has(e.target))
  const keep = new Set<string>(inDomain)
  for (const n of data.nodes) if (n.self) keep.add(n.id)
  for (const e of edges) {
    keep.add(e.source)
    keep.add(e.target)
  }
  return { nodes: data.nodes.filter((n) => keep.has(n.id)), edges }
}

export const MONO = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace"

// Entity + relation vocabulary (mirrors the crm domain manifest in diwa/domains/builtin.py)
export const ENTITY_TYPES = ['person', 'company', 'event'] as const
export const WORLDS = ['fund', 'fi', 'vc', 'markets', 'media', 'studio', 'signal', 'other'] as const
export const RELATION_TYPES = [
  'knows',
  'works_at',
  'founder_of',
  'member_of',
  'invested_in',
  'reports_to',
  'had_interaction',
] as const
